import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from 'src/constants'
import { getLocalStorageTokens, getRefreshToken } from 'src/utils/getToken'

const DEFAULT_SQUAD_API = 'https://api.squad.skalx.dev'

export type SquadConfig = {
  baseUrl: string
  agentId: string | null
}

export const getSquadConfig = (): SquadConfig => {
  const baseUrl = (
    import.meta.env.VITE_APP_SQUAD_API?.trim() || DEFAULT_SQUAD_API
  ).replace(/\/$/, '')
  const agentId = import.meta.env.VITE_APP_SQUAD_AGENT_ID?.trim() || null

  return { baseUrl, agentId }
}

/** True when base URL + default agent id + a stored access token are all present. */
export const isSquadConfigured = (): boolean => {
  const { agentId } = getSquadConfig()
  const { access } = getLocalStorageTokens()

  return Boolean(agentId && access)
}

export class SquadConfigError extends Error {
  constructor(message = 'Squad agent is not configured') {
    super(message)
    this.name = 'SquadConfigError'
  }
}

export class SquadRequestError extends Error {
  status: number
  body: string

  constructor(status: number, body: string) {
    super(`Squad request failed (${status})`)
    this.name = 'SquadRequestError'
    this.status = status
    this.body = body
  }
}

const authHeaders = (appToken: string): Record<string, string> => {
  const { access } = getLocalStorageTokens()
  if (!access) {
    throw new SquadConfigError('Not signed in')
  }

  return {
    Authorization: `Token ${access}`,
    'Saas-App-Token': appToken
  }
}

/**
 * fetch wrapper that retries once on 401 after refreshing the access token —
 * replicates what the axios interceptor does for the rest of the app.
 */
export const squadFetch = async (
  path: string,
  init: RequestInit,
  appToken: string
): Promise<Response> => {
  const { baseUrl } = getSquadConfig()
  const url = `${baseUrl}${path}`

  const doFetch = () =>
    fetch(url, {
      ...init,
      headers: { ...authHeaders(appToken), ...(init.headers ?? {}) }
    })

  let response = await doFetch()
  if (response.status === 401) {
    const refresh = window.localStorage.getItem(REFRESH_TOKEN_KEY)
    if (refresh) {
      try {
        const next = await getRefreshToken(refresh)
        window.localStorage.setItem(ACCESS_TOKEN_KEY, next.access)
        response = await doFetch()
      } catch {
        // fall through with the original 401
      }
    }
  }

  return response
}

export const jsonOrThrow = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }

  return (await response.json()) as T
}

export type SquadAgent = {
  id: string
  name: string
  description?: string
}

export const listAgents = async (appToken: string): Promise<SquadAgent[]> => {
  const response = await squadFetch('/api/agents/', { method: 'GET' }, appToken)
  const data = await jsonOrThrow<SquadAgent[] | { results: SquadAgent[] }>(
    response
  )

  return Array.isArray(data) ? data : (data.results ?? [])
}

/* ------------------------------------------------------------------ *
 * Agent CRUD (real backend — replaces the mock agent registry)
 * ------------------------------------------------------------------ */

/** RBAC governance tier — who owns an agent and who may edit it. */
export type SquadAgentTier = 'system' | 'template' | 'workspace' | 'personal'

/** How many of the CALLING user's own unattended triggers hang off an agent.
 * Non-zero means sharing the agent lets colleagues reshape automations that keep
 * running with the caller's permissions. */
export type SquadRunAsExposure = {
  schedules: number
  channels: number
  widgets: number
  total: number
}

/** A run-only assignee (a `viewer` collaborator). */
export type SquadAgentAssignee = {
  id: number
  user_sso_id: string
  role: string
  invited_by_sso_id?: string
  created_at?: string
}

/** Full agent record (read serializer) — includes the delegated-auth ceiling. */
export type SquadAgentDetail = {
  id: string
  name: string
  description?: string
  system_prompt?: string
  provider?: string
  model?: string
  temperature?: number
  restricted?: boolean
  /** RBAC tier: system (Skalx-owned, read-only for clients) | template | workspace | personal. */
  tier?: SquadAgentTier
  /** True for Skalx-owned cross-tenant System agents. */
  is_platform?: boolean
  /** THIS caller's own triggers on this agent — drives the sharing warning.
   * There is no per-agent execution identity: every run acts as a real user
   * (the caller in chat, or a trigger's run-as user when unattended). */
  run_as_exposure?: SquadRunAsExposure
  /** Space-delimited Core scope-catalog codes — the agent's capability ceiling. */
  allowed_scopes: string
  /** Run-only assignees (viewer collaborators). */
  assignees?: SquadAgentAssignee[]
  /** Tool-execution autonomy: 'ask' (gate write tools) | 'auto' (autonomous). */
  tool_execution_mode?: string
  /** THIS caller's own "Always approve" exceptions — never another user's, even
   * on a shared/platform agent. Read-only; revoke via `revokeAutoApprovedTool`. */
  auto_approved_tools?: string[]
  mcp_server_count?: number
  skill_count?: number
  can_edit?: boolean
  can_run?: boolean
  is_owner?: boolean
  created_at?: string
  modified_at?: string
}

/** Writable fields (write serializer). `tier` accepts personal|workspace|template. */
export type SquadAgentWriteInput = {
  name?: string
  description?: string
  system_prompt?: string
  provider?: string
  model?: string
  temperature?: number
  restricted?: boolean
  tier?: Exclude<SquadAgentTier, 'system'>
  allowed_scopes?: string
  tool_execution_mode?: string
  /** Not a stored field: records that the caller confirmed the "your triggers
   * keep running as you" warning while widening the agent's audience. */
  run_as_exposure_ack?: boolean
}

export const jsonInit = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {})
})

export const getAgent = async (
  appToken: string,
  agentId: string
): Promise<SquadAgentDetail> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/`,
    { method: 'GET' },
    appToken
  )

  return jsonOrThrow<SquadAgentDetail>(response)
}

export const createAgent = async (
  appToken: string,
  body: SquadAgentWriteInput
): Promise<SquadAgentDetail> => {
  const response = await squadFetch(
    '/api/agents/',
    jsonInit('POST', body),
    appToken
  )

  return jsonOrThrow<SquadAgentDetail>(response)
}

export const updateAgent = async (
  appToken: string,
  agentId: string,
  patch: SquadAgentWriteInput
): Promise<SquadAgentDetail> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/`,
    jsonInit('PATCH', patch),
    appToken
  )

  return jsonOrThrow<SquadAgentDetail>(response)
}

export const deleteAgent = async (
  appToken: string,
  agentId: string
): Promise<void> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/`,
    { method: 'DELETE' },
    appToken
  )
  if (!response.ok && response.status !== 404) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }
}

/** Revokes ONE of the caller's own "Always approve" exceptions for this
 * agent — scoped server-side to the caller's own sso_id, never another
 * user's. There is no "set the whole list" write: a shared agent has no
 * single list to overwrite. */
export const revokeAutoApprovedTool = async (
  appToken: string,
  agentId: string,
  toolName: string
): Promise<void> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/auto_approved_tools/${encodeURIComponent(toolName)}/`,
    { method: 'DELETE' },
    appToken
  )
  if (!response.ok && response.status !== 404) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }
}

/* ------------------------------------------------------------------ *
 * Run-only assignees (viewer collaborators) — the `/assignees/` endpoints.
 * Assigning a user to a `restricted` agent lets them run/chat it but not edit.
 * ------------------------------------------------------------------ */

export const listAgentAssignees = async (
  appToken: string,
  agentId: string
): Promise<SquadAgentAssignee[]> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/assignees/`,
    { method: 'GET' },
    appToken
  )

  return jsonOrThrow<SquadAgentAssignee[]>(response)
}

export const addAgentAssignee = async (
  appToken: string,
  agentId: string,
  userSsoId: string
): Promise<SquadAgentAssignee> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/assignees/`,
    jsonInit('POST', { user_sso_id: userSsoId }),
    appToken
  )

  return jsonOrThrow<SquadAgentAssignee>(response)
}

export const removeAgentAssignee = async (
  appToken: string,
  agentId: string,
  assigneeId: number
): Promise<void> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/assignees/${assigneeId}/`,
    { method: 'DELETE' },
    appToken
  )
  if (!response.ok && response.status !== 404) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }
}

/* ------------------------------------------------------------------ *
 * Turn feedback (👍/👎) — via the Squad observability proxy
 * ------------------------------------------------------------------ */

/**
 * Per-run rating map for a thread, keyed by Squad `run_id`. `up` is null when
 * the run's trace exists but is unrated; a run with no trace simply isn't
 * present. Lets the chat render each answer's current 👍/👎.
 */
export type SquadRunRating = { trace_id: string; up: boolean | null }
export type SquadThreadRatings = {
  enabled?: boolean
  ratings: Record<string, SquadRunRating>
}

export const getThreadRatings = async (
  appToken: string,
  threadId: string
): Promise<SquadThreadRatings> => {
  const response = await squadFetch(
    `/api/observability/threads/${threadId}/ratings/`,
    { method: 'GET' },
    appToken
  )

  return jsonOrThrow<SquadThreadRatings>(response)
}

/**
 * Rate a chat turn 👍/👎 by its Squad `run_id`. Set-only: the proxy never
 * deletes (self-hosted Opik silently drops every future score on a trace whose
 * rating was once cleared) and verifies the write landed, so a thrown error
 * here means the rating genuinely didn't stick.
 */
export const rateRun = async (
  appToken: string,
  runId: string,
  up: boolean
): Promise<void> => {
  const response = await squadFetch(
    `/api/observability/runs/${runId}/feedback/`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ up })
    },
    appToken
  )
  await jsonOrThrow<unknown>(response)
}
